"use client"

import { Head, router } from "@inertiajs/react"
import { BarChartSquare02 } from "@untitledui/icons"

import {
  type ReachRow,
  ReachTable,
} from "@/components/admin/onwards/reach-table"
import { EmptyState } from "@/components/ui/empty-state"
import { PaginationCardMinimal } from "@/components/ui/pagination"
import { DashboardLayout } from "@/layouts/dashboard-layout"
import { adminOnwardsReachPath } from "@/routes"
import type { Pagination } from "@/types/contents/pagination"

interface Totals {
  impressions: number
  referrals: number
  uptake_pct: number
}

interface ReachProps {
  rows: ReachRow[]
  totals: Totals
  pagination: Pagination
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-secondary flex flex-col gap-1 rounded-xl border p-5">
      <span className="text-tertiary text-sm">{label}</span>
      <span className="text-primary font-display text-display-sm font-semibold tabular-nums">
        {value}
      </span>
    </div>
  )
}

export default function OnwardsReach({ rows, totals, pagination }: ReachProps) {
  return (
    <DashboardLayout>
      <Head title="Reach & referrals" />
      <main className="mx-auto flex w-full max-w-4xl flex-col gap-8 px-4 pt-8 pb-16 lg:px-8">
        <header className="flex flex-col gap-1">
          <h1 className="text-primary font-display md:text-display-xs text-xl font-semibold">
            Reach &amp; referrals
          </h1>
          <p className="text-md text-tertiary">
            Every time a support service was shown, and every time someone
            stepped out to it. Counts come straight from the Onwards ledger —
            reach survives catalogue edits, so deleted destinations keep their
            history.
          </p>
        </header>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <StatCard label="Impressions" value={String(totals.impressions)} />
          <StatCard label="Referrals" value={String(totals.referrals)} />
          <StatCard label="Uptake" value={`${totals.uptake_pct}%`} />
        </div>

        {rows.length === 0 ? (
          <EmptyState size="sm" className="py-10">
            <EmptyState.Header pattern="none">
              <EmptyState.FeaturedIcon
                color="gray"
                theme="modern"
                icon={BarChartSquare02}
              />
            </EmptyState.Header>
            <EmptyState.Content>
              <p className="text-primary text-md text-center font-semibold">
                Nothing surfaced yet
              </p>
              <EmptyState.Description>
                Impressions land here the first time a viewer reaches an outro
                with a live slate.
              </EmptyState.Description>
            </EmptyState.Content>
          </EmptyState>
        ) : (
          <ReachTable
            rows={rows}
            footer={
              pagination.total > 1 ? (
                <PaginationCardMinimal
                  page={pagination.page}
                  total={pagination.total}
                  onPageChange={(page) =>
                    router.get(
                      adminOnwardsReachPath(),
                      { page },
                      { preserveState: true, preserveScroll: true },
                    )
                  }
                />
              ) : undefined
            }
          />
        )}
      </main>
    </DashboardLayout>
  )
}
